import { axios } from '@/services/axios';
import { Box, HStack, Skeleton, SkeletonCircle, Stack } from '@chakra-ui/react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';
import { isEmpty } from 'lodash';
import { useDefinedAttributes } from 'pages/services/serviceprofiles/vendorDefined/DefinedAttributesContext';
import DefinedAttributes from 'pages/services/serviceprofiles/vendorDefined/attributes/definedAttributes';
import useDefinedAttributeValidation from 'pages/services/serviceprofiles/vendorDefined/attributes/useDefinedAttributeValidation';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import FormBase from '@/components/forms/new/FormBase';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetVendorDefinedTemplate from './queries/useGetVendorDefinedTemplate';

export default function AddVendorDefined({ serviceWizard }) {
    const { requiredScope } = useSelector((state) => state.login);
    const handleBackButton = () => serviceWizard.reset();

    const [error, setError] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const { currentCompany: companyID } = useSelector(
        (state) => state.navigationLists,
    );
    const { dispatch, attributesList, attributesErrors } =
        useDefinedAttributes();

    const { handleAttributeError, removeAttributeError } =
        useDefinedAttributeValidation();

    // to be completed
    const DEFAULT_VENDOR_DEFINED = {
        name: '',
        serviceDefinitionID: '',
    };

    const [data, setData] = useState(DEFAULT_VENDOR_DEFINED);
    const hasBypassedForm = useRef(false);

    const createVendorDefinedService = async ({
        name,
        serviceDefinitionID,
        attributeList,
    }) => {
        // close add service confirmation modal
        setIsLoading(true);
        setError(null);

        try {
            await axios.post('service/vendordefined', {
                name,
                companyID: companyID,
                serviceDefinitionID,
                attributeList,
            });

            toast.success('Successfully created a Vendor Defined Service!');
            // go back to dashboard services
            handleBackButton();
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const {
        isInitialLoading: isTemplateLoading,
        isFetching: vendorDefinedLoading,
    } = useGetVendorDefinedTemplate({
        companyID,
        serviceProfile: serviceWizard.selectedServiceProfile,
        onSuccess: async (data) => {
            // data for the service type and attributes list
            setData((prevState) => ({
                ...prevState,
                ...data,
            }));
            if (data.attributeList?.length > 0) {
                for (let att of data.attributeList) {
                    // attribute value is empty
                    if (isEmpty(att.value)) {
                        // default value is empty, set value to be empty string
                        att.value = isEmpty(att.defaultValue)
                            ? null
                            : att.defaultValue;
                    }
                }
            }
            dispatch({
                type: 'UPDATE_ATT_LIST',
                payload: data.attributeList,
            });

            // (3084) The template only carries the attributes the user's
            // scope can see. With none to fill in, skip this form and add
            // the service straight away. If that fails, the form shows the
            // error and Save retries.
            if (!data.attributeList?.length && !hasBypassedForm.current) {
                hasBypassedForm.current = true;
                createVendorDefinedService({
                    name: data.name ?? DEFAULT_VENDOR_DEFINED.name,
                    serviceDefinitionID:
                        data.serviceDefinitionID ??
                        DEFAULT_VENDOR_DEFINED.serviceDefinitionID,
                    attributeList: data.attributeList ?? [],
                });
            }
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
            handleBackButton();
        },
    });

    const handleValidation = () => {
        setError(null);
        let hasError = false;

        // check for any attributes errors
        if (attributesErrors.length > 0) {
            hasError = true;
        }

        // check for required attributes
        if (attributesList?.length > 0) {
            attributesList.forEach((att, index) => {
                const validation = JSON.parse(
                    att.definedAttributeValidationType,
                );

                // if required, input cannot be empty
                if (validation.Required && isEmpty(att.value)) {
                    handleAttributeError(index, 'Field is required.');
                    hasError = true;
                } else {
                    removeAttributeError(index);
                }
            });
        }
        if (hasError) {
            setError({
                response: {
                    data: {
                        message:
                            'There are some validation errors in the form. Please check and try again.',
                    },
                },
            });
            return;
        }

        // validation passes, proceed with submit
        handleSubmit();
    };

    const handleSubmit = () =>
        createVendorDefinedService({
            name: data.name,
            serviceDefinitionID: data.serviceDefinitionID,
            attributeList: attributesList,
        });

    // Hold the form back until the template says whether it is needed (3084).
    if (isLoading || isTemplateLoading) {
        return <LoadingIndicator />;
    }

    const breadCrumbTrail = [
        { label: 'Dashboard', path: ['', { index: 0 }] },
        { label: 'Add Service', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Add ${serviceWizard.selectedServiceProfile?.serviceDefinition?.name || 'Vendor Defined Service'}`}
                subTitleHeader={true}
                initialValues={DEFAULT_VENDOR_DEFINED}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                finalFormSubmit={handleValidation}>
                {() => (
                    <>
                        {error && (
                            <Alert status="error" sx={{ marginTop: '1rem' }}>
                                <AlertIcon />
                                <AlertDescription>
                                    {error?.response?.data?.message ||
                                        'An error occured. please try again later.'}
                                </AlertDescription>
                            </Alert>
                        )}

                        {vendorDefinedLoading ? (
                            <Box>
                                {[...Array(3)].map((_, index) => (
                                    <Stack key={index} mb={3}>
                                        <HStack>
                                            <Skeleton h={5} w="25%" />
                                            <SkeletonCircle h={5} w={5} />
                                        </HStack>

                                        <Skeleton h={10} borderRadius={4} />
                                        <Skeleton h={5} w="15%" />
                                    </Stack>
                                ))}
                            </Box>
                        ) : attributesList?.length > 0 ? (
                            <DefinedAttributes requiredScope={requiredScope} />
                        ) : (
                            <Alert status="info">
                                <AlertIcon />
                                No information is required at this time! Click
                                &apos;Save&apos; to continue.
                            </Alert>
                        )}
                    </>
                )}
            </FormBase>
        </>
    );
}
